import { useEffect, useState } from "react";
import { MapPin, History, AlertTriangle } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button, Field, Input, Select, Textarea } from "../../components/ui";
import { recordAttendance, updateAttendance } from "../../api/attendance";
import Coordinates from "../../components/Coordinates";
import { fieldErrors, errorMessage, formatDateTime, todayISO } from "../../lib/records";
import { ATTENDANCE_STATUSES, ATTENDANCE_STATUS_LABELS } from "../../lib/enums";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
}

function toISO(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function PunchLocation({ label, punch }) {
  if (!punch?.at) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2">
      <span className="w-8 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </span>
      <Coordinates location={punch.location} size="md" />
    </div>
  );
}

export default function AttendanceEntryModal({ open, onClose, record, employees = [], onSaved }) {
  const isCorrection = !!record?._id;

  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [status, setStatus] = useState("");
  const [checkInAt, setCheckInAt] = useState("");
  const [checkOutAt, setCheckOutAt] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeId(record?.employee?._id ?? record?.employee ?? "");
    setDate(record?.date ?? todayISO());
    setStatus(record?.status ?? "");
    setCheckInAt(toLocalInput(record?.checkIn?.at));
    setCheckOutAt(toLocalInput(record?.checkOut?.at));
    setNote(record?.note ?? "");
    setErrors({});
    setFormError("");
  }, [open, record]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");

    if (!status && !checkInAt && !checkOutAt && !note.trim()) {
      setFormError("Set a status, a punch time, or a note — there is nothing to save yet.");
      return;
    }
    if (checkInAt && checkOutAt && new Date(checkOutAt) < new Date(checkInAt)) {
      setErrors({ checkOutAt: "Check-out cannot be before check-in." });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(status ? { status } : {}),
        ...(checkInAt ? { checkInAt: toISO(checkInAt) } : {}),
        ...(checkOutAt ? { checkOutAt: toISO(checkOutAt) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      };

      if (isCorrection) {
        await updateAttendance(record._id, payload);
      } else {
        await recordAttendance(employeeId, { date, ...payload });
      }
      onSaved();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const employeeName = record?.employee?.name;
  const logs = record?.logs ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCorrection ? "Correct this day" : "Record a day"}
      description={
        isCorrection
          ? `${employeeName ?? "Employee"} · ${record?.date ?? ""}`
          : "For a missed punch, approved leave, a holiday, or a week off."
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        {!isCorrection && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Employee" required error={errors.employee}>
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="">Choose someone…</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                    {emp.designation ? ` — ${emp.designation}` : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Date"
              error={errors.date}
              hint="Calling this twice for a day corrects it, never duplicates it."
            >
              <Input
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </Field>
          </div>
        )}

        <Field
          label="Status"
          error={errors.status}
          hint="A status you set explicitly always beats the shift rules."
        >
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          >
            <option value="">Leave as classified</option>
            {ATTENDANCE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ATTENDANCE_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Check in" error={errors.checkInAt}>
            <Input
              type="datetime-local"
              value={checkInAt}
              onChange={(e) => setCheckInAt(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field label="Check out" error={errors.checkOutAt}>
            <Input
              type="datetime-local"
              value={checkOutAt}
              onChange={(e) => setCheckOutAt(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <Field label="Note" error={errors.note}>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Approved leave"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>
        {isCorrection && (record?.checkIn?.location || record?.checkOut?.location) && (
          <div className="rounded-md bg-emerald-50/60 px-3 py-2.5">
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <MapPin size={13} className="text-emerald-600" /> Where they punched
            </p>
            <div className="space-y-1">
              <PunchLocation label="In" punch={record.checkIn} />
              <PunchLocation label="Out" punch={record.checkOut} />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              The original GPS fix is kept as evidence — only the timestamp moves, and the punch is
              marked as corrected by you rather than recorded by the device.
            </p>
          </div>
        )}

        <p className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          This changes the hours their pay is computed from, and the employee is notified.
        </p>

        {/* Audit trail — every punch and correction, with a before/after diff. */}
        {isCorrection && logs.length > 0 && (
          <div className="rounded-md border border-emerald-100 bg-emerald-50/30 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <History size={13} className="text-emerald-600" /> Audit trail
            </p>
            <ul className="max-h-32 space-y-1 overflow-y-auto text-xs">
              {logs.map((log, i) => (
                <li key={i} className="rounded bg-white/70 px-2.5 py-1.5 text-gray-600">
                  <span className="font-medium text-gray-800">{log.action?.replace(/_/g, " ")}</span>
                  <span className="text-gray-400"> · {formatDateTime(log.at)}</span>
                  {log.byType && <span className="text-gray-400"> · by {log.byType}</span>}
                  {log.changes &&
                    Object.entries(log.changes).map(([field, [before, after]]) => (
                      <span key={field} className="ml-1 block text-gray-500">
                        {field}: {String(before)} → <strong>{String(after)}</strong>
                      </span>
                    ))}
                  {log.note && <span className="ml-1 block italic text-gray-500">“{log.note}”</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            {isCorrection ? "Save correction" : "Record day"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
